import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import fontLatin from "./assets/fonts/wix-madefor-text-latin.woff2";
import { lazy, Suspense, useMemo, type ComponentType } from "react";
import { appRoutes } from "./routes";

// Route-based code splitting: each landing page ships its own chunk so a
// visitor only downloads the code for the route they land on.
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

export type AppProps = {
  /**
   * Router wrapper. Defaults to BrowserRouter in the browser; the prerenderer
   * passes StaticRouter so the server renders the exact same component tree.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Router?: ComponentType<any>;
  location?: string;

  /**
   * Route component that is already loaded (no Suspense fallback during the
   * first render). Both the prerenderer and the client hydration path supply
   * this so server markup and first client render are identical.
   */
  preloadedPath?: string;
  PreloadedComponent?: ComponentType;
  helmetContext?: object;
};

const App = ({
  Router = BrowserRouter,
  location,
  preloadedPath,
  PreloadedComponent,
  helmetContext,
}: AppProps) => {
  const routes = useMemo(
    () =>
      appRoutes.map((r) => ({
        path: r.path,
        Component:
          PreloadedComponent && preloadedPath === r.path
            ? PreloadedComponent
            : lazy(r.importer),
      })),
    [preloadedPath, PreloadedComponent]
  );

  const routerProps = location ? { location } : {};

  return (
    <HelmetProvider context={helmetContext}>
      <Helmet>
        <link rel="preload" as="font" type="font/woff2" crossOrigin="anonymous" href={fontLatin} />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Router {...routerProps}>
            <Suspense fallback={<div className="min-h-screen bg-white" />}>
              <Routes>
                {routes.map(({ path, Component }) => (
                  <Route key={path} path={path} element={<Component />} />
                ))}
                {/* ADD ALL CUSTOM ROUTES IN src/routes.tsx, ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Router>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
